// PDFPreview component for displaying PDF documents with navigation and zoom
// Uses react-pdf library for PDF rendering

import React, { useState, useCallback, useEffect } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'

// Configure worker for react-pdf
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`

// Utility function for class names
const classNames = (...classes: (string | undefined | false)[]): string => {
  return classes.filter(Boolean).join(' ')
}

// Zoom levels
const ZOOM_LEVELS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2]

export interface PDFPreviewProps {
  /** PDF file to preview */
  file: File
  /** Whether to show as thumbnail (small) or full preview */
  mode: 'thumbnail' | 'full'
  /** Custom width for the preview */
  width?: number
  /** Custom height for the preview */
  height?: number
  /** Whether to show navigation controls */
  showControls?: boolean
  /** Whether to show zoom controls */
  showZoom?: boolean
  /** Callback when page changes */
  onPageChange?: (pageNumber: number) => void
  /** Callback when zoom changes */
  onZoomChange?: (zoom: number) => void
  /** Additional CSS classes */
  className?: string
}

const PDFPreview: React.FC<PDFPreviewProps> = ({
  file,
  mode = 'full',
  width,
  height,
  showControls = true,
  showZoom = true,
  onPageChange,
  onZoomChange,
  className = ''
}) => {
  const [numPages, setNumPages] = useState<number>(0)
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [scale, setScale] = useState<number>(1)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [fileUrl, setFileUrl] = useState<string | null>(null)

  // Create file URL for PDF.js
  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file)
      setFileUrl(url)
      
      return () => {
        URL.revokeObjectURL(url)
      }
    }
  }, [file])

  // Handle PDF load success
  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages)
    setLoading(false)
    setError(null)
  }, [])

  // Handle PDF load error
  const onDocumentLoadError = useCallback((error: Error) => {
    console.error('PDF load error:', error)
    setError('Failed to load PDF document')
    setLoading(false)
  }, [])

  // Navigate to previous page
  const goToPreviousPage = useCallback(() => {
    if (pageNumber > 1) {
      const newPage = pageNumber - 1
      setPageNumber(newPage)
      onPageChange?.(newPage)
    }
  }, [pageNumber, onPageChange])

  // Navigate to next page
  const goToNextPage = useCallback(() => {
    if (pageNumber < numPages) {
      const newPage = pageNumber + 1
      setPageNumber(newPage)
      onPageChange?.(newPage)
    }
  }, [pageNumber, numPages, onPageChange])

  // Change page directly
  const goToPage = useCallback((page: number) => {
    if (page >= 1 && page <= numPages) {
      setPageNumber(page)
      onPageChange?.(page)
    }
  }, [numPages, onPageChange])

  // Zoom in
  const zoomIn = useCallback(() => {
    const currentIndex = ZOOM_LEVELS.indexOf(scale)
    if (currentIndex < ZOOM_LEVELS.length - 1) {
      const newScale = ZOOM_LEVELS[currentIndex + 1]
      setScale(newScale)
      onZoomChange?.(newScale)
    }
  }, [scale, onZoomChange])

  // Zoom out
  const zoomOut = useCallback(() => {
    const currentIndex = ZOOM_LEVELS.indexOf(scale)
    if (currentIndex > 0) {
      const newScale = ZOOM_LEVELS[currentIndex - 1]
      setScale(newScale)
      onZoomChange?.(newScale)
    }
  }, [scale, onZoomChange])

  // Reset zoom
  const resetZoom = useCallback(() => {
    setScale(1)
    onZoomChange?.(1)
  }, [onZoomChange])

  // Get container dimensions
  const getContainerDimensions = () => {
    if (mode === 'thumbnail') {
      return {
        width: width || 120,
        height: height || 160
      }
    }
    
    return {
      width: width || 600,
      height: height || 800
    }
  }

  const containerDimensions = getContainerDimensions()

  if (!fileUrl) {
    return (
      <div className={classNames('tw-flex tw-items-center tw-justify-center tw-bg-gray-100 tw-rounded', className)}>
        <span className="tw-text-gray-500 tw-text-sm">Loading PDF...</span>
      </div>
    )
  }

  return (
    <div className={classNames('tw-flex tw-flex-col tw-bg-white tw-rounded-lg tw-overflow-hidden', className)}>
      {/* Controls */}
      {mode === 'full' && showControls && (
        <div className="tw-flex tw-items-center tw-justify-between tw-p-4 tw-border-b tw-border-gray-200 tw-bg-gray-50">
          {/* Page navigation */}
          <div className="tw-flex tw-items-center tw-gap-2">
            <button
              type="button"
              onClick={goToPreviousPage}
              disabled={pageNumber <= 1}
              className="tw-p-2 tw-rounded tw-bg-white tw-border tw-border-gray-300 tw-text-gray-700 hover:tw-bg-gray-50 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors"
              aria-label="Previous page"
            >
              <span className="material-symbols-outlined tw-text-[18px]">chevron_left</span>
            </button>
            
            <div className="tw-flex tw-items-center tw-gap-2 tw-px-3">
              <input
                type="number"
                min={1}
                max={numPages}
                value={pageNumber}
                onChange={(e) => goToPage(parseInt(e.target.value, 10))}
                className="tw-w-16 tw-px-2 tw-py-1 tw-text-sm tw-border tw-border-gray-300 tw-rounded tw-text-center"
              />
              <span className="tw-text-sm tw-text-gray-600">of {numPages}</span>
            </div>
            
            <button
              type="button"
              onClick={goToNextPage}
              disabled={pageNumber >= numPages}
              className="tw-p-2 tw-rounded tw-bg-white tw-border tw-border-gray-300 tw-text-gray-700 hover:tw-bg-gray-50 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors"
              aria-label="Next page"
            >
              <span className="material-symbols-outlined tw-text-[18px]">chevron_right</span>
            </button>
          </div>

          {/* Zoom controls */}
          {showZoom && (
            <div className="tw-flex tw-items-center tw-gap-2">
              <button
                type="button"
                onClick={zoomOut}
                disabled={scale <= ZOOM_LEVELS[0]}
                className="tw-p-2 tw-rounded tw-bg-white tw-border tw-border-gray-300 tw-text-gray-700 hover:tw-bg-gray-50 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors"
                aria-label="Zoom out"
              >
                <span className="material-symbols-outlined tw-text-[18px]">zoom_out</span>
              </button>
              
              <button
                type="button"
                onClick={resetZoom}
                className="tw-px-3 tw-py-1 tw-text-sm tw-bg-white tw-border tw-border-gray-300 tw-rounded tw-text-gray-700 hover:tw-bg-gray-50 tw-transition-colors"
              >
                {Math.round(scale * 100)}%
              </button>
              
              <button
                type="button"
                onClick={zoomIn}
                disabled={scale >= ZOOM_LEVELS[ZOOM_LEVELS.length - 1]}
                className="tw-p-2 tw-rounded tw-bg-white tw-border tw-border-gray-300 tw-text-gray-700 hover:tw-bg-gray-50 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors"
                aria-label="Zoom in"
              >
                <span className="material-symbols-outlined tw-text-[18px]">zoom_in</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* PDF Document Container */}
      <div className={classNames(
        'tw-flex tw-items-center tw-justify-center tw-overflow-auto tw-bg-gray-100',
        mode === 'thumbnail' ? 'tw-h-full' : 'tw-flex-1'
      )}>
        {loading && (
          <div className="tw-flex tw-items-center tw-justify-center tw-p-8">
            <div className="tw-flex tw-items-center tw-gap-3">
              <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-b-2 tw-border-blue-600"></div>
              <span className="tw-text-gray-600 tw-text-sm">Loading PDF...</span>
            </div>
          </div>
        )}
        
        {error && (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-8 tw-text-center">
            <span className="material-symbols-outlined tw-text-red-400 tw-text-4xl tw-mb-2">error</span>
            <h4 className="tw-text-sm tw-font-medium tw-text-gray-900 tw-mb-1">PDF Preview Error</h4>
            <p className="tw-text-xs tw-text-gray-500">{error}</p>
          </div>
        )}

        {!loading && !error && fileUrl && (
          <div className="tw-p-4">
            <Document
              file={fileUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={
                <div className="tw-flex tw-items-center tw-justify-center tw-p-8">
                  <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-b-2 tw-border-blue-600"></div>
                </div>
              }
              error={
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-8 tw-text-center">
                  <span className="material-symbols-outlined tw-text-red-400 tw-text-4xl tw-mb-2">error</span>
                  <h4 className="tw-text-sm tw-font-medium tw-text-gray-900 tw-mb-1">Failed to Load PDF</h4>
                  <p className="tw-text-xs tw-text-gray-500">This PDF file could not be displayed.</p>
                </div>
              }
            >
              <Page
                pageNumber={mode === 'thumbnail' ? 1 : pageNumber}
                scale={mode === 'thumbnail' ? 0.2 : scale}
                width={mode === 'thumbnail' ? containerDimensions.width : undefined}
                loading={
                  <div className={classNames(
                    'tw-flex tw-items-center tw-justify-center tw-bg-gray-50 tw-border tw-border-dashed tw-border-gray-300 tw-rounded',
                    mode === 'thumbnail' ? 'tw-h-full' : 'tw-h-96'
                  )}>
                    <div className="tw-animate-spin tw-rounded-full tw-h-4 tw-w-4 tw-border-b-2 tw-border-blue-600"></div>
                  </div>
                }
                error={
                  <div className={classNames(
                    'tw-flex tw-flex-col tw-items-center tw-justify-center tw-bg-gray-50 tw-border tw-border-dashed tw-border-gray-300 tw-rounded',
                    mode === 'thumbnail' ? 'tw-h-full tw-text-xs' : 'tw-h-96'
                  )}>
                    <span className="material-symbols-outlined tw-text-red-400 tw-text-2xl tw-mb-1">error</span>
                    <span className="tw-text-gray-600">Page Error</span>
                  </div>
                }
                className="tw-border tw-border-gray-300 tw-rounded tw-shadow-sm"
              />
            </Document>
          </div>
        )}
      </div>

      {/* Thumbnail mode page counter */}
      {mode === 'thumbnail' && numPages > 0 && (
        <div className="tw-absolute tw-bottom-1 tw-right-1 tw-bg-black tw-bg-opacity-75 tw-text-white tw-px-1 tw-py-0.5 tw-rounded tw-text-xs">
          {numPages} pages
        </div>
      )}
    </div>
  )
}

export default PDFPreview